import { FC } from 'react';
import { FormProvider } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { twMerge } from 'tailwind-merge';
import { EnrichedStopPlace } from '../../../../../../types';
import { SimpleButton } from '../../../../../common/Buttons';
import {
  ReasonForChangeForm,
  ValidityPeriodForm,
} from '../../../../../common/Forms';
import { FormRow, Row } from '../../../../../common/LayoutComponents';
import { CopyStopAreaSuccessResult } from '../../Types';
import { useCopyStopAreaFormUtils } from '../Utils';
import { CopyStopAreaCutConfirmationModal } from './CopyStopAreaCutConfirmationModal';

const testIds = {
  form: 'CopyStopAreaForm::form',
  submitButton: 'CopyStopAreaForm::submitButton',
  cancelButton: 'CopyStopAreaForm::cancelButton',
};

type CopyStopAreaFormProps = {
  readonly className?: string;
  readonly onCancel: () => void;
  readonly onCopyCreated: (result: CopyStopAreaSuccessResult) => void;
  readonly stopArea: EnrichedStopPlace;
};

export const CopyStopAreaForm: FC<CopyStopAreaFormProps> = ({
  className,
  onCancel,
  onCopyCreated,
  stopArea,
}) => {
  const { t } = useTranslation();

  const {
    methods,
    cutConfirmationState,
    onFormSubmit,
    onCutConfirmationSubmit,
    cancelCutConfirmation,
  } = useCopyStopAreaFormUtils(stopArea, onCopyCreated);

  return (
    <FormProvider {...methods}>
      <form
        onSubmit={methods.handleSubmit(onFormSubmit)}
        className={twMerge(`space-y-4`, className)}
        data-testid={testIds.form}
      >
        <FormRow>
          <ReasonForChangeForm />
        </FormRow>

        <FormRow>
          <ValidityPeriodForm />
        </FormRow>

        <Row className="-mx-12 -mb-8 justify-end gap-4 border border-light-grey bg-background px-12 py-2">
          <SimpleButton
            inverted
            onClick={onCancel}
            testId={testIds.cancelButton}
          >
            {t(($) => $.cancel)}
          </SimpleButton>
          <SimpleButton type="submit" testId={testIds.submitButton}>
            {t(($) => $.save)}
          </SimpleButton>
        </Row>
      </form>
      {!!cutConfirmationState && (
        <CopyStopAreaCutConfirmationModal
          modalState={cutConfirmationState}
          onClose={cancelCutConfirmation}
          onConfirm={methods.handleSubmit(onCutConfirmationSubmit)}
        />
      )}
    </FormProvider>
  );
};
